import React from 'react';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { Home, NavigateNext } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbLabels } from '../../../context/BreadcrumbContext';

// Breadcrumbs show where a page sits (built from the URL), not how you got
// there — "back" is the page's back button. Record ids are shown by name,
// reported by the page through useBreadcrumbLabel (see BreadcrumbContext).

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1, 0),
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  '& .MuiBreadcrumbs-separator': {
    margin: theme.spacing(0, 1),
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

const CurrentPage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 'medium',
  // Long record names shouldn't push the trail onto several lines
  maxWidth: 420,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
}));

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRecordId = (segment) => UUID_REGEX.test(segment) || /^\d+$/.test(segment);

// URL prefixes that group pages but aren't pages themselves (no route renders
// them), so they get no crumb: /inventory/items is just "Varer".
const NON_PAGES = [
  /^\/inventory$/,
  /^\/tidsregistering$/,
  /^\/admin$/,
  /^\/setup$/,
  /^\/files\/view$/,
  /^\/rooms\/[^/]+\/equipment$/,
  /^\/delegations\/[^/]+\/revisions$/,
];

// URL segment → i18n key (mostly the sidebar's own labels, so both always match)
const SEGMENT_KEYS = {
  '': 'common:navigation.dashboard',
  'dashboard': 'common:navigation.dashboard',
  'home': 'common:navigation.home',
  'staff': 'common:navigation.staff_members',
  'roles': 'common:navigation.roles',
  'departments': 'common:navigation.departments',
  'modules': 'common:navigation.system_modules',
  'permissions': 'common:breadcrumbs.permissions',
  'notifications': 'common:navigation.notifications',
  'errors': 'common:navigation.error_management',
  'browse': 'common:navigation.browse',
  'tasks': 'common:navigation.tasks',
  'calendar': 'common:navigation.calender',
  'team-calendar': 'common:navigation.team_calender',
  'categories': 'common:navigation.categories',
  'rooms': 'common:navigation.rooms',
  'equipment': 'common:navigation.equipment',
  'infoscreens': 'common:navigation.infoscreens',
  'items': 'common:navigation.inventory_items',
  'suppliers': 'common:navigation.inventory_suppliers',
  'batches': 'common:navigation.inventory_batches',
  'msds': 'common:navigation.inventory_msds',
  'test-barcodes': 'common:navigation.inventory_test_barcodes',
  'procedures': 'common:navigation.procedures',
  'delegations': 'common:navigation.delegations',
  'contacts': 'common:navigation.contacts',
  'reports': 'common:navigation.reports',
  'stock-value': 'common:breadcrumbs.stock_value',
  'abc': 'common:breadcrumbs.abc_analysis',
  'co-removal': 'common:breadcrumbs.co_removal',
  'files': 'common:navigation.files',
  'settings': 'common:navigation.settings',
  'feedback': 'common:navigation.feedback',
  'changelog': 'common:navigation.changelog',
  'software-licenses': 'common:navigation.software_licenses',
  'templates': 'common:navigation.template_admin',
  'fravaer': 'common:navigation.tidsregistering_absence',
  'fremmøde': 'common:navigation.tidsregistering_attendance',
  'mine-dage': 'common:navigation.tidsregistering_mine_days',
  'saldi': 'common:navigation.tidsregistering_balances',
  'sygefravaer': 'common:navigation.tidsregistering_sick_leave',
  'periodelukning': 'common:navigation.tidsregistering_period_closing',
  'loeneksport': 'common:navigation.tidsregistering_salary_export',
  'godkendelser': 'common:navigation.tidsregistering_approvals',
  'analyse': 'common:navigation.tidsregistering_analyse',
  'profile': 'common:user_menu.profile',
  'create': 'common:breadcrumbs.create',
  'new': 'common:breadcrumbs.create',
  'edit': 'common:breadcrumbs.edit',
  'catalog': 'common:breadcrumbs.catalog',
  'select-template': 'common:breadcrumbs.select_template',
  'search': 'common:breadcrumbs.search',
  'scanner': 'common:breadcrumbs.scanner',
  'download': 'common:breadcrumbs.download',
  'parse': 'common:breadcrumbs.parse',
};

// Collection segment → singular label for a /<collection>/<uuid> detail page
const ENTITY_KEYS = {
  'equipment': 'common:breadcrumbs.entity.equipment',
  'tasks': 'common:breadcrumbs.entity.task',
  'rooms': 'common:breadcrumbs.entity.room',
  'procedures': 'common:breadcrumbs.entity.procedure',
  'staff': 'common:breadcrumbs.entity.staff_member',
  'items': 'common:breadcrumbs.entity.item',
  'suppliers': 'common:breadcrumbs.entity.supplier',
  'batches': 'common:breadcrumbs.entity.batch',
  'infoscreens': 'common:breadcrumbs.entity.infoscreen',
  'delegations': 'common:breadcrumbs.entity.delegation',
  'contacts': 'common:breadcrumbs.entity.contact',
  'roles': 'common:breadcrumbs.entity.role',
  'departments': 'common:breadcrumbs.entity.department',
  'software-licenses': 'common:breadcrumbs.entity.license',
  'feedback': 'common:breadcrumbs.entity.feedback',
  'revisions': 'common:breadcrumbs.entity.revision',
  'view': 'common:breadcrumbs.entity.file',
};

// Pages that are the root of the trail themselves
const HIDDEN_ON = new Set(['/', '/home']);

const decode = (segment) => {
  try { return decodeURIComponent(segment); } catch { return segment; }
};

/** [{ path, label }] for every crumb after "Hjem", last one = current page. */
export function buildCrumbs(pathname, t, labels = {}) {
  const segments = pathname.split('/').filter(Boolean).map(decode);
  const crumbs = [];
  segments.forEach((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    if (NON_PAGES.some((re) => re.test(path))) return;

    let label;
    if (isRecordId(segment)) {
      const collection = segments[index - 1] ?? '';
      label = labels[segment]
        ?? (ENTITY_KEYS[collection] ? t(ENTITY_KEYS[collection]) : t('common:breadcrumbs.details', { name: collection }));
    } else if (SEGMENT_KEYS[segment]) {
      label = t(SEGMENT_KEYS[segment]);
    } else {
      label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    }
    crumbs.push({ path, label });
  });
  return crumbs;
}

export default function DynamicBreadcrumbs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const labels = useBreadcrumbLabels();

  if (HIDDEN_ON.has(pathname)) return null;
  const crumbs = buildCrumbs(pathname, t, labels);
  if (crumbs.length === 0) return null;

  const go = (e, path) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <BreadcrumbContainer>
      <StyledBreadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
        <BreadcrumbLink href="/home" onClick={(e) => go(e, '/home')}>
          <Home fontSize="small" />
          {t('common:navigation.home')}
        </BreadcrumbLink>
        {crumbs.map(({ path, label }, index) => (
          index === crumbs.length - 1 ? (
            <CurrentPage key={path} aria-current="page" title={label}>{label}</CurrentPage>
          ) : (
            <BreadcrumbLink key={path} href={path} onClick={(e) => go(e, path)}>
              {label}
            </BreadcrumbLink>
          )
        ))}
      </StyledBreadcrumbs>
    </BreadcrumbContainer>
  );
}
